/**
 * RIG live adapter — pure transform from the v7 relationship payload (+ the
 * signal brief and action history) into a RigViewModel. No I/O; the page layer
 * fetches, this shapes. Kept pure so it is node-testable.
 *
 * Wiring rulings applied:
 *  - STOP 1 / v7: bind per-contact employer from employerOrganizationDisplayName
 *    and the waypoint from consortiumOrganizationDisplayName; render as stored.
 *  - STOP 2: ONE organization-level warm card from warm_path + the best delivery
 *    row's echo (no per-contact A/B).
 *  - STOP 3: case_study_status is a status → case-study slot honestly absent;
 *    delivery snippet is the engagement hover (→ graph hex), not a case study.
 *  - STOP 4 (RIG-DENSITY-1): rank by strength → temperature → recency; show at
 *    most 3 individuals per side, overflow participants collapse to a cluster;
 *    temperature drives node SIZE in the layout (not visibility).
 *  - STOP 5: PII deferred to an owner/admin-gated by-id endpoint; contact.pii is
 *    null until it lands, and the modal renders an honest ownership state
 *    ("Owned by {owner}" + directional copy), never a pending apology.
 */

import type {
  BasisFamily,
  RigChip,
  RigCluster,
  RigContact,
  RigDelivery,
  RigPatternRead,
  RigViewModel,
  RigWarmPath,
  TextSegment,
  Temperature,
} from "./types";
import type {
  PatternReadProjection,
  RelationshipContactPayload,
  RelationshipDeliveryItem,
  RigHistoryInput,
  RigSignalInput,
  SignalRelationshipResultV7,
} from "./payload";
import {
  abbreviatePractice,
  basisClass,
  basisMix,
  daysAgo,
  entrySummary,
  formatDayMonYear,
  normalizeTemperature,
  ownerInitial,
  relationshipPhrase,
  temperatureBand,
} from "./logic";

const MAX_PER_ORG = 3;

const STRENGTH_RANK: Record<string, number> = { strong: 0, moderate: 1, weak: 2 };
const TEMP_RANK: Record<Temperature, number> = { hot: 0, warm: 1, cooling: 2, cold: 3 };

const SIGNAL_TYPE_LABEL: Record<string, string> = {
  rfp: "RFP",
  hiring: "Hiring",
  m_and_a: "M&A",
  transformation: "Transformation",
  leadership: "Leadership",
  market_intel: "Market intel",
  target_account: "Target account",
};

interface FlatContact {
  p: RelationshipContactPayload;
  ownerName: string | null;
}

/** strength → temperature → recency (more recent first). */
function rankContacts(a: FlatContact, b: FlatContact): number {
  const s = (STRENGTH_RANK[a.p.strength] ?? 9) - (STRENGTH_RANK[b.p.strength] ?? 9);
  if (s !== 0) return s;
  const t =
    TEMP_RANK[normalizeTemperature(a.p.temperature)] -
    TEMP_RANK[normalizeTemperature(b.p.temperature)];
  if (t !== 0) return t;
  const ra = a.p.interaction_recency ? Date.parse(a.p.interaction_recency) : 0;
  const rb = b.p.interaction_recency ? Date.parse(b.p.interaction_recency) : 0;
  return (Number.isNaN(rb) ? 0 : rb) - (Number.isNaN(ra) ? 0 : ra);
}

function dominant(values: string[]): string | null {
  if (values.length === 0) return null;
  const counts = new Map<string, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  let best: string | null = null;
  let bestN = 0;
  for (const [v, n] of counts) {
    if (n > bestN) {
      best = v;
      bestN = n;
    }
  }
  return best;
}

function caseStudyEcho(status: string): string {
  const s = status.toLowerCase();
  if (s === "pending") return "CASE STUDY PENDING";
  if (s === "" || s === "none") return "NO CASE STUDY";
  return `CASE STUDY: ${status.toUpperCase()}`;
}

function toRigContact(
  f: FlatContact,
  basis: BasisFamily,
  signalCompany: string | null,
  waypointName: string | null,
  nowMs: number,
): RigContact {
  const p = f.p;
  const company = p.employerOrganizationDisplayName ?? (basis === "direct" ? signalCompany : null);
  return {
    id: p.contactId,
    name: p.name,
    title: p.title,
    company,
    basis,
    phrase: relationshipPhrase(basis, signalCompany, waypointName),
    owner: f.ownerName,
    ownerInit: ownerInitial(f.ownerName),
    sourceGroup: p.source_group,
    dateMet: p.date_met ? formatDayMonYear(p.date_met) : null,
    touches: p.interaction_count,
    lastDays: daysAgo(p.interaction_recency, nowMs),
    alsoMatches: 0,
    temp: normalizeTemperature(p.temperature),
    active: p.active_engagement,
    pii: null,
  };
}

function toRigDelivery(d: RelationshipDeliveryItem): RigDelivery {
  return {
    practice: d.practice,
    practiceAbbrev: abbreviatePractice(d.practice),
    descriptor: d.descriptor,
    caseStudy: null,
    caseStudyStatus: d.case_study_status,
    valueTier: d.value_tier,
    status: d.active_dormant === "Active" ? "Active" : "Dormant",
    snippet: d.snippet,
  };
}

function bestDelivery(deliveries: RelationshipDeliveryItem[]): RelationshipDeliveryItem | null {
  return deliveries.find((d) => d.active_dormant === "Active") ?? deliveries[0] ?? null;
}

function composeWarmPath(
  warmPathName: string,
  best: RelationshipDeliveryItem | null,
  companyName: string | null,
): RigWarmPath | null {
  if (!warmPathName || !best) return null;
  const echo = [
    best.practice.toUpperCase(),
    caseStudyEcho(best.case_study_status),
    best.value_tier ? `VALUE TIER ${best.value_tier.toUpperCase()}` : null,
    best.active_dormant.toUpperCase(),
  ]
    .filter(Boolean)
    .join(" · ");
  const line: TextSegment[] = [
    { text: "Via " },
    { text: warmPathName, bold: true },
    {
      text: ` — cross-practice route${companyName ? ` into ${companyName}` : ""} (fallback; no named carrier).`,
    },
  ];
  return { line, cta: `Route via ${warmPathName}`, echo };
}

function hostLabel(url: string | null): string {
  if (!url) return "Source";
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return "Source";
  }
}

// Readable verb phrasing, mirroring the Signal modal's VERB_PHRASE
// (ModalRail.tsx) so RIG history reads the same as the signal surface.
const VERB_PHRASE: Record<string, string> = {
  claim: "Claimed the signal",
  unclaim: "Released the claim",
  assign: "Assigned the signal",
  share: "Created a Magic Link",
  archive_with_reason: "Archived the signal",
  follow_up_set: "Set a follow-up",
  relationship_check_run: "Ran a relationship check",
  tariff_scan_run: "Ran a Tariff Terrain scan",
  asset_sent: "Sent an asset",
  context_note: "Added context",
  magic_link_open: "Opened the Magic Link",
  signal_saved: "Saved the signal",
  signal_unsaved: "Removed from saved",
  contact_search_run: "Ran a contact search",
  stage_outreach_made: "Recorded outreach made",
  stage_meeting_scheduled: "Recorded a meeting",
  stage_proposal: "Recorded a proposal",
  stage_regressed: "Walked back a stage",
  outcome_recorded: "Recorded the outcome",
  outcome_reopened: "Reopened the pursuit",
};

function humanizeVerb(verb: string): string {
  const phrase = VERB_PHRASE[verb];
  if (phrase) return phrase;
  const spaced = verb.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** A readable ledger line: phrase · actor · date, omitting absent parts (no raw
 *  event tokens, honest-absence). */
function historyLine(h: RigHistoryInput): string {
  return [humanizeVerb(h.verb), h.actorName, formatDayMonYear(h.at)]
    .filter(Boolean)
    .join(" · ");
}

export interface AdaptOptions {
  nowMs: number;
}

/** Map a payload Pattern Read (snake_case) to the RIG view-model six-block shape.
 *  Coerces watch_for defensively; the server guarantees the sealed structure. */
function toRigPatternRead(p: PatternReadProjection): RigPatternRead {
  return {
    patternName: p.pattern_name,
    expectedNextDevelopment: p.expected_next_development,
    whyNow: p.why_now,
    pursuitSurface: p.pursuit_surface,
    recommendedNextAction: p.recommended_next_action,
    watchFor: Array.isArray(p.watch_for) ? p.watch_for.map((w) => String(w)) : [],
    evidence: {
      requirement: p.evidence?.requirement ?? "",
      confidenceLevel: p.evidence?.confidence_level ?? "",
    },
  };
}

export function toRigViewModel(
  rel: SignalRelationshipResultV7,
  signal: RigSignalInput | null,
  history: RigHistoryInput[] | null,
  opts: AdaptOptions,
): RigViewModel {
  const signalCompany = rel.companyName ?? signal?.company ?? null;
  const waypointName = rel.consortiumOrganizationDisplayName ?? "Consortium";

  // Flatten owner edges → contacts, classify basis, drop excluded.
  const flat: { f: FlatContact; basis: BasisFamily }[] = [];
  for (const edge of rel.relationships) {
    for (const p of edge.contacts) {
      const cls = basisClass(p.matchBasis);
      if (cls === "excluded") continue;
      flat.push({ f: { p, ownerName: edge.ownerConsultantName }, basis: cls });
    }
  }

  const directsAll = flat.filter((x) => x.basis === "direct").sort((a, b) => rankContacts(a.f, b.f));
  const participantsAll = flat
    .filter((x) => x.basis === "participant")
    .sort((a, b) => rankContacts(a.f, b.f));

  const directsShown = directsAll.slice(0, MAX_PER_ORG);
  const directOverflow = directsAll.slice(MAX_PER_ORG);
  const participantsShown = participantsAll.slice(0, MAX_PER_ORG);
  const participantOverflow = participantsAll.slice(MAX_PER_ORG);

  const hasParticipants = participantsAll.length > 0;
  const effectiveWaypoint = hasParticipants ? waypointName : null;

  const contacts: RigContact[] = [
    ...directsShown.map((x) => toRigContact(x.f, "direct", signalCompany, effectiveWaypoint, opts.nowMs)),
    ...participantsShown.map((x) =>
      toRigContact(x.f, "participant", signalCompany, effectiveWaypoint, opts.nowMs),
    ),
  ];

  // RIG-DENSITY-1: each org caps at 3 shown + a cluster for the rest, on both
  // the direct and participant sides.
  const buildCluster = (
    basis: BasisFamily,
    overflow: { f: FlatContact; basis: BasisFamily }[],
    fallbackCompany: string | null,
  ): RigCluster | null =>
    overflow.length === 0
      ? null
      : {
          basis,
          count: overflow.length,
          company:
            dominant(
              overflow
                .map((x) => x.f.p.employerOrganizationDisplayName)
                .filter((v): v is string => Boolean(v)),
            ) ?? fallbackCompany,
          basisMix: basisMix(overflow.length, basis),
          tempBand: temperatureBand(
            overflow.map((x) => normalizeTemperature(x.f.p.temperature)),
          ),
          contacts: overflow.map((x) =>
            toRigContact(x.f, basis, signalCompany, effectiveWaypoint, opts.nowMs),
          ),
        };
  const directCluster = buildCluster("direct", directOverflow, signalCompany);
  const participantCluster = buildCluster("participant", participantOverflow, null);
  const clusters = [directCluster, participantCluster].filter(
    (c): c is RigCluster => c != null,
  );
  const clusteredTotal = (directCluster?.count ?? 0) + (participantCluster?.count ?? 0);

  const deliveries = rel.delivery.map(toRigDelivery);
  const best = bestDelivery(rel.delivery);
  const warmPath = composeWarmPath(rel.warm_path, best, signalCompany);

  // Account-level posture facts read from ALL matched contacts (flat), not the
  // shown top-3 — a recently-touched or active contact that ranks into the
  // overflow still counts toward the account's last-touch / active-engagement.
  const existingClient = rel.delivery.length > 0;
  const activeEngagement =
    flat.some((x) => x.f.p.active_engagement) ||
    rel.delivery.some((d) => d.active_dormant === "Active");
  const valueTier = best?.value_tier ?? "";

  const category = (
    signal?.eventType ||
    (signal ? SIGNAL_TYPE_LABEL[signal.signalType] ?? signal.signalType : "")
  ).toUpperCase();
  const verification = (signal?.verification ?? "").toUpperCase();
  const date = formatDayMonYear(signal?.firstObservedAt ?? signal?.lastObservedAt ?? null);

  const lastTouchIso = flat
    .map((x) => x.f.p.interaction_recency)
    .filter((v): v is string => Boolean(v))
    .sort()
    .at(-1);

  const chips: RigChip[] = [];
  if (category) chips.push({ label: category, kind: "plain" });
  if (verification) chips.push({ label: verification, kind: "plain" });
  if (date) chips.push({ label: date, kind: "plain" });
  if (existingClient) chips.push({ label: "EXISTING CLIENT", kind: "solid" });
  if (activeEngagement) chips.push({ label: "ACTIVE ENGAGEMENT", kind: "led" });
  if (valueTier) chips.push({ label: `VALUE TIER · ${valueTier.toUpperCase()}`, kind: "value" });

  const totalDirect = directsAll.length;
  const totalParticipant = participantsAll.length;
  const matched = totalDirect + totalParticipant;
  const ledger: string[] = [
    `${matched} CONTACTS MATCHED (${totalDirect} DIRECT · ${totalParticipant} PARTICIPANT)`,
    // Most recent first (the projected action history is chronological, oldest-first).
    ...(history ?? []).slice().reverse().map(historyLine),
  ];

  const headline = signal?.headline ?? signalCompany ?? "Relationship intelligence";

  return {
    signalId: rel.signalId,
    entry: {
      headline,
      summary: entrySummary(
        directsShown.length,
        participantsShown.length,
        clusteredTotal,
        existingClient ? "Existing client" : "Prospect",
      ),
      chips,
    },
    brief: {
      headline,
      category,
      verification,
      date,
      summary: signal?.summary ?? "",
      whyItMatters: signal?.whyItMatters ?? "",
      dealSize: null,
      evidence: {
        sourceLabel: hostLabel(signal?.url ?? null),
        sourceUrl: signal?.url ?? null,
        retrieved: formatDayMonYear(signal?.lastObservedAt ?? null),
        published: formatDayMonYear(signal?.firstObservedAt ?? null),
        status: verification,
        deadline: signal?.deadlineAt ? formatDayMonYear(signal.deadlineAt) : null,
      },
    },
    contacts,
    clusters,
    deliveries,
    activation: {
      posture: {
        existingClient,
        valueTier,
        activeEngagement,
        lastTouch: lastTouchIso ? formatDayMonYear(lastTouchIso) : null,
      },
      warmPath,
      history: ledger,
    },
    // Authored Pattern Reads from the payload (v8), mapped to the sealed six-block
    // projection. [] when none authored → the panel's honest empty state. Never
    // fabricated client-side.
    patternReads: (rel.patternReads ?? []).map(toRigPatternRead),
    orgs: [
      { id: "signal", name: signalCompany ?? "", role: "signal" },
      ...(hasParticipants
        ? [
            {
              id: "participant",
              name:
                dominant(
                  participantsShown
                    .map((x) => x.f.p.employerOrganizationDisplayName)
                    .filter((v): v is string => Boolean(v)),
                ) ?? "",
              role: "participant" as const,
            },
          ]
        : []),
    ],
    waypoint: hasParticipants ? { name: waypointName } : null,
  };
}
